import React from 'react';
import { VideoEmbed, getCurrentWeek } from './clientUtils';
import ExerciseBlocksRenderer from './ExerciseBlocksRenderer';
import { Button, Badge } from './components/primitives';
import { NEUTRAL, STATUS_COLORS } from './designTokens';

function ClientBlockView({
  selectedBlock,
  selectedCp,
  selectedProgram,
  blockExercises,
  exerciseLogs,
  weekSessionCounts,
  toggleExercise,
  markAllDone,
  onBack,
  brandAccent,
  theme,
  reviewMode,
}) {
  const doneCount = exerciseLogs.length;
  const totalCount = blockExercises.length;
  const allDone = totalCount > 0 && doneCount === totalCount;
  const progress = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px 16px', background: NEUTRAL.page, minHeight: '100vh' }}>
      <Button
        theme={theme}
        variant="secondary"
        onClick={onBack}
        style={{ marginBottom: '20px' }}
      >
        ← Tillbaka till block
      </Button>

      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 8px', color: NEUTRAL.ink, fontSize: '1.35em', fontWeight: '700' }}>{selectedBlock.title}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Badge theme={theme} variant="neutral">
            Vecka {selectedBlock.start_week}–{selectedBlock.end_week}
          </Badge>
          {selectedBlock.description && (
            <span style={{ color: NEUTRAL.muted, fontSize: '0.875em' }}>{selectedBlock.description}</span>
          )}
        </div>
      </div>

      {/* Framstegsindikator – döljs i review mode */}
      {!reviewMode && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', border: `1px solid ${NEUTRAL.line}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontWeight: '600', color: allDone ? STATUS_COLORS.success : NEUTRAL.ink, fontSize: '0.92em' }}>
              {allDone ? 'Alla övningar klara idag!' : `${doneCount} av ${totalCount} klara idag`}
            </span>
            {!allDone && totalCount > 0 && (
              <Button theme={theme} size="sm" onClick={markAllDone}>
                Markera alla klara
              </Button>
            )}
          </div>
          <div style={{ background: NEUTRAL.line, borderRadius: '4px', height: '6px' }}>
            <div style={{ background: allDone ? STATUS_COLORS.success : brandAccent, width: `${progress}%`, height: '6px', borderRadius: '4px', transition: 'width 0.3s ease' }} />
          </div>
          {selectedCp && selectedCp.frequency_per_week > 0 && (
            <div style={{ marginTop: '10px', fontSize: '0.8em', color: NEUTRAL.muted }}>
              Vecka {getCurrentWeek(selectedCp)} · {weekSessionCounts[selectedProgram?.id] || 0} av {selectedCp.frequency_per_week} pass genomförda denna vecka
            </div>
          )}
        </div>
      )}

      {reviewMode && (
        <p style={{ margin: '0 0 16px', fontSize: '0.78em', color: NEUTRAL.muted }}>
          Översikt – veckan är redan klar.
        </p>
      )}

      {blockExercises.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 20px' }}>
          <p style={{ color: NEUTRAL.muted, margin: 0 }}>Inga övningar i detta block ännu.</p>
        </div>
      ) : (
        blockExercises.map((pe, index) => {
          const isDone = exerciseLogs.find(l => l.exercise_id === pe.exercise_id);
          return (
            <div key={pe.id} style={{
              background: (!reviewMode && isDone) ? STATUS_COLORS.success_bg : 'white',
              border: `1px solid ${(!reviewMode && isDone) ? STATUS_COLORS.success_bg : NEUTRAL.line}`,
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '10px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    margin: '0 0 6px',
                    fontSize: '0.97em',
                    fontWeight: '600',
                    color: (!reviewMode && isDone) ? NEUTRAL.muted : NEUTRAL.ink,
                    textDecoration: (!reviewMode && isDone) ? 'line-through' : 'none',
                  }}>
                    {index + 1}. {pe.exercise?.title}
                  </h3>
                  {pe.exercise?.category && (
                    <Badge theme={theme} variant="neutral">{pe.exercise.category}</Badge>
                  )}
                  {(pe.sets || pe.reps || pe.duration_seconds || pe.rest_seconds) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '8px' }}>
                      {pe.sets && (
                        <Badge theme={theme} variant="accent">{pe.sets} set</Badge>
                      )}
                      {pe.reps && (
                        <Badge theme={theme} variant="accent">{pe.reps} rep</Badge>
                      )}
                      {pe.duration_seconds && (
                        <Badge theme={theme} variant="accent">{pe.duration_seconds}s</Badge>
                      )}
                      {pe.rest_seconds && (
                        <Badge theme={theme} variant="neutral">{pe.rest_seconds}s vila</Badge>
                      )}
                    </div>
                  )}
                  {pe.notes && (
                    <p style={{ margin: '7px 0 0', color: NEUTRAL.muted, fontSize: '0.83em', lineHeight: '1.45', fontStyle: 'italic' }}>
                      {pe.notes}
                    </p>
                  )}
                </div>
                {!reviewMode && (
                  <Button
                    theme={theme}
                    variant={isDone ? 'secondary' : 'primary'}
                    size="sm"
                    onClick={() => toggleExercise(pe.exercise_id)}
                    style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {isDone ? 'Ångra' : 'Klar'}
                  </Button>
                )}
              </div>
              {pe.exercise?.blocks?.length > 0 ? (
                <ExerciseBlocksRenderer blocks={pe.exercise.blocks} brandAccent={brandAccent} />
              ) : (
                <>
                  {pe.exercise?.description && (
                    <p style={{ margin: '10px 0 0', color: NEUTRAL.muted, fontSize: '0.875em', lineHeight: '1.55' }}>
                      {pe.exercise.description}
                    </p>
                  )}
                  {pe.exercise?.image_url && (
                    <img src={pe.exercise.image_url} alt={pe.exercise.title} style={{ maxWidth: '100%', borderRadius: '8px', marginTop: '12px' }} />
                  )}
                  {pe.exercise?.video_url && <VideoEmbed url={pe.exercise.video_url} />}
                </>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

export default ClientBlockView;
